import { withStyles } from '@material-ui/core/styles';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
  compose,
  lifecycle,
  setPropTypes,
  withHandlers,
  withState
} from 'recompose';
import { bindActionCreators } from 'redux';
import {
  Drawer,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import moment from 'moment';
import { isAuthenticated, sendLogin } from '../../auth/Auth';
import Header from '../../components/Header';
import MenuDrawer from '../../components/MenuDrawer';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import ShashokuTicketTable from '../organisms/ShashokuTicketTable';
import SearchButton from '../../components/atoms/SearchButton';
import { getAccounts } from '../../actions/Accounts';
import {
  getTickets,
  GET_TICKETS_SUCCESS,
  updateTicketsStatus,
  downloadTickets,
  UPDATE_TICKETS_STATUS_SUCCESS,
  DOWNLOAD_TICKETS_SUCCESS
} from '../../actions/Tickets';
import ConfirmDialog from '../../components/molecules/ConfirmDialog';
import { getSystems } from '../../actions/Systems';
import CsvDownloadButton from '../../components/atoms/CsvDownloadButton';

const styles = {
  root: {},
  searchForm: {
    padding: '10px 26px 10px 26px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account,
      systems: state.systems.list
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          getAccounts,
          getTickets,
          updateTicketsStatus,
          getSystems,
          downloadTickets
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openDrawer', 'updateOpenDrawer', false),
  withState('fromSystemId', 'setFromSystemId', null),
  withState('displayName', 'setDisplayName', null),
  withState('shopName', 'setShopName', null),
  withState('menuName', 'setMenuName', null),
  withState('fromDate', 'setFromDate', null),
  withState('toDate', 'setToDate', null),
  withState('page', 'setPage', 1),
  withState('openTable', 'updateOpenTable', false),
  withState('rowsPerPage', 'setRowsPerPage', 100),
  withState('loading', 'updateLoading', false),
  withState('openProgress', 'updateOpenProgress', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('openConfirmDialog', 'setOpenConfirmDialog', false),
  withState('selectedTicketIds', 'setSelectedTicketIds', []),
  withState('errorMessage', 'setErrorMessage', ''),
  withState('downloadParams', 'setDownloadParams', {}),
  withState('checked', 'setChecked', () => {
    return new Set();
  }),
  withHandlers({
    checkSession: props => data => {
      if (
        data.error === true &&
        typeof data.payload !== 'undefined' &&
        data.payload.status === 403
      ) {
        sendLogin(props);
      }
      return data;
    }
  }),
  withHandlers({
    csvDownloadAction: props => async () => {
      props.updateLoading(true);
      props.updateOpenProgress(true);
      const response = await props.actions
        .downloadTickets(props.downloadParams)
        .then(data => props.checkSession(data));
      if (response.type === DOWNLOAD_TICKETS_SUCCESS) {
        const timeStamp = moment()
          .tz('Asia/Tokyo')
          .format('YYYYMMDDHHmm');
        props.updateOpenProgress(false);
        props.setResultMessage('');
        props.updateLoading(false);
        const blob = response.payload;
        const fileName = `${timeStamp}_shashoku_ticket_info.csv`;

        if (window.navigator.msSaveOrOpenBlob) {
          // for IE,Edge
          window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
          // for chrome, firefox
          const url = URL.createObjectURL(
            new Blob([blob], { type: 'text/csv' })
          );
          const linkEl = document.createElement('a');
          linkEl.href = url;
          linkEl.setAttribute('download', fileName);
          document.body.appendChild(linkEl);
          linkEl.click();

          linkEl.parentNode.removeChild(linkEl);
          setTimeout(() => {
            URL.revokeObjectURL(url);
          }, 1000);
        }
      } else {
        props.setResultMessage('データエクスポートに失敗しました。');
        props.updateLoading(false);
      }
    }
  }),
  withHandlers({
    handleChangePage: props => (event, page, limit) => {
      const limitNum = limit || props.rowsPerPage;
      console.log('HANDLE CHANGE PAGE');
      const table = document.getElementById('scrollTableId');
      if (table !== null) {
        table.scrollTop = 0;
      }
      props.setPage(page);
      const searchKey = {
        fromSystemId: props.fromSystemId,
        displayName: props.displayName,
        shopName: props.shopName,
        menuName: props.menuName,
        ticketUseDateFrom: props.fromDate,
        ticketUseDateTo: props.toDate,
        page,
        limit: limitNum
      };

      props.setDownloadParams(searchKey);

      props.updateLoading(true);
      props.updateOpenProgress(true);
      props.actions.getTickets(searchKey).then(data => {
        props.checkSession(data);
        if (data.type === GET_TICKETS_SUCCESS) {
          props.updateOpenProgress(false);
          props.setResultMessage('');
          props.updateLoading(false);
          props.updateOpenTable(true);
        } else {
          props.setErrorMessage('エラーが発生しました！');
          props.updateLoading(false);
          props.updateOpenProgress(false);
        }
      });
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
      this.props.actions.getAccounts({ cognitoUserId: '' }).then(async data => {
        this.props.checkSession(data);
        this.props.actions.getSystems();
        this.props.handleChangePage(null, 1, this.props.rowsPerPage);
      });
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    openDrawer,
    updateOpenDrawer,
    account,
    openTable,
    updateOpenTable,
    openProgress,
    updateOpenProgress,
    page,
    handleChangePage,
    loading,
    updateLoading,
    rowsPerPage,
    setRowsPerPage,
    resultMessage,
    setResultMessage,
    fromSystemId,
    setFromSystemId,
    displayName,
    setDisplayName,
    shopName,
    setShopName,
    menuName,
    setMenuName,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    openConfirmDialog,
    setOpenConfirmDialog,
    selectedTicketIds,
    setSelectedTicketIds,
    checked,
    setChecked,
    systems,
    errorMessage,
    setErrorMessage,
    csvDownloadAction
  }) => {
    function uncheckAll() {
      setChecked(new Set());
    }

    const searchAction = async () => {
      updateOpenTable(true);
      handleChangePage(null, 1, rowsPerPage);
    };

    const progress = {
      loading,
      updateLoading,
      openProgress,
      updateOpenProgress,
      setResultMessage,
      setErrorMessage
    };

    const onChangeTicketStatus = ids => {
      if (ids.length === 0) {
        setErrorMessage('データを選択してください。');
        return;
      }
      setSelectedTicketIds(ids);
      setOpenConfirmDialog(true);
    };

    const changeTicketStatus = ids => {
      setOpenConfirmDialog(false);
      updateLoading(true);
      updateOpenProgress(true);
      actions.updateTicketsStatus({ shashokuTicketIds: ids }).then(data => {
        checkSession(data);
        if (data.type === UPDATE_TICKETS_STATUS_SUCCESS) {
          uncheckAll();
          updateOpenProgress(false);
          setResultMessage('');
          updateLoading(false);
          handleChangePage(null, page, rowsPerPage);
        } else {
          setErrorMessage('エラーが発生しました！');
          updateLoading(false);
          updateOpenProgress(false);
        }
      });
    };

    const progressOkAction = () => {
      updateOpenProgress(false);
      updateLoading(true);
      handleChangePage(null, page, rowsPerPage);
    };
    return (
      <div className={classes.root}>
        <Header
          onMenuClick={() => {
            updateOpenDrawer(true);
          }}
        />
        <Drawer
          open={openDrawer}
          anchor="left"
          onClose={() => {
            updateOpenDrawer(false);
          }}
        >
          <div tabIndex={0} role="button">
            <MenuDrawer account={account} />
          </div>
        </Drawer>
        <div className={classes.pageTitileDiv}>
          <Typography variant="h6" color="textSecondary">
            社食ごちめしのチケット編集
          </Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.searchForm}>
            <TextField
              select
              label="会社名"
              className={classes.textField}
              margin="normal"
              value={fromSystemId}
              onChange={e => {
                setFromSystemId(e.target.value || '');
              }}
              InputLabelProps={{
                shrink: fromSystemId
              }}
            >
              <MenuItem id="">選択なし</MenuItem>
              {systems.map(item => {
                return (
                  <MenuItem
                    key={item.connectSystemId}
                    value={item.connectSystemId}
                  >
                    {item.connectSystemName}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              label="社員名"
              type="search"
              className={classes.textField}
              margin="normal"
              value={displayName}
              onChange={e => {
                setDisplayName(e.target.value);
              }}
            />
            <TextField
              label="メニュー名"
              type="search"
              className={classes.textField}
              margin="normal"
              value={menuName}
              onChange={e => {
                setMenuName(e.target.value);
              }}
            />
            <TextField
              label="システムID"
              type="search"
              className={classes.textField}
              margin="normal"
              value={fromSystemId}
              onChange={e => {
                const id =
                  parseInt(e.target.value.replace(/[^\d]/, ''), 0) || '';
                setFromSystemId(id);
              }}
              InputLabelProps={{
                shrink: fromSystemId
              }}
            />
            <TextField
              label="店舗名"
              type="search"
              className={classes.textField}
              margin="normal"
              value={shopName}
              onChange={e => {
                setShopName(e.target.value);
              }}
            />
            <TextField
              label="利用日(From)"
              type="date"
              className={classes.textField}
              margin="normal"
              value={fromDate}
              inputProps={{
                max: toDate
              }}
              InputLabelProps={{
                shrink: true
              }}
              onChange={e => {
                setFromDate(e.target.value);
              }}
            />
            <TextField
              label="利用日(To)"
              type="date"
              className={classes.textField}
              margin="normal"
              value={toDate}
              inputProps={{
                min: fromDate
              }}
              InputLabelProps={{
                shrink: true
              }}
              onChange={e => {
                setToDate(e.target.value);
              }}
            />
            <div className={classes.buttonGroupLeft}>
              <SearchButton onClickAction={searchAction} />
              <CsvDownloadButton
                title="CSV出力"
                onClickAction={csvDownloadAction}
              />
            </div>
          </div>

          {openTable && (
            <ShashokuTicketTable
              page={page}
              handleChangePage={handleChangePage}
              progress={progress}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              setResultMessage={setResultMessage}
              updateOpenProgress={updateOpenProgress}
              updateLoading={updateLoading}
              onChangeTicketStatus={onChangeTicketStatus}
              selectedTicketIds={selectedTicketIds}
              checked={checked}
              setChecked={setChecked}
            />
          )}
        </Paper>
        <ConfirmDialog
          openConfirm={openConfirmDialog}
          updateOpenConfirm={setOpenConfirmDialog}
          message="この社食チケットを未使用に戻しますか。"
          okAction={() => changeTicketStatus(selectedTicketIds)}
        />
        <ProgressDialog
          openProgress={openProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />

        <ProgressDialog
          openProgress={errorMessage !== ''}
          progressOkAction={() => setErrorMessage('')}
          loading={false}
          resultMessage={errorMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
